import { Input, InputNumber } from '@aigc/antd';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import { tryURL } from '@aigc/toolkit';
import {
  CloudUploadOutlined,
  PaperClipOutlined,
  PictureOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Attachments, Sender } from '@ant-design/x';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import {
  App,
  Button,
  Divider,
  Dropdown,
  Form,
  Segmented,
  Select,
  Space,
  Tooltip,
  type GetRef,
} from 'antd';
import { useRef } from 'react';
import { Controller, useForm, type DefaultValues } from 'react-hook-form';
import { v7 as uuid } from 'uuid';
import { number, object, string, type InferType } from 'yup';
import { FormControl } from '../../../components/form-control';
import { UploadedImageListModal } from '../../../components/uploaded-image-list-modal';
import { createBeforeUpload, createUploadValidator } from '../../file';
import type { FileDto } from '../../file/model';
import { fileSchema } from '../../file/schema';
import { toFileInputDtoList } from '../../file/utils';
import type { ImageGenerationTaskDto } from '../../task/model';
import type { ScriptSource } from '../../video/model';
import { generateImageMutaionOptions } from '../api';
import {
  DEFAULT_IMAGE_MODEL_CODE,
  imageModelSpecs,
  type ImageModelCode,
} from '../image-llm';

const image_generation_schema = object({
  model: string<ImageModelCode>().required('请选择模型'),
  name: string().nullable(),
  file_list: fileSchema.max(14),
  prompt: string().label('提示词').nullable().required(),
  aspect_ratio: string().nullable(),
  quantity: number().nullable(),
});

export function useImageGenerationForm(
  default_values: DefaultValues<InferType<typeof image_generation_schema>> = {},
) {
  return useForm({
    defaultValues: {
      model: DEFAULT_IMAGE_MODEL_CODE,
      aspect_ratio: '9:16',
      quantity: 1,
      ...default_values,
    },
    resolver: yupResolver(image_generation_schema),
  });
}

export type ImageGenerationFormInstance = ReturnType<
  typeof useImageGenerationForm
>;

export function useImageGenerationSubmit(
  form: ImageGenerationFormInstance,
  options: {
    source: ScriptSource | undefined;
    successMessage?: string;
    onSuccess?: () => Promise<void> | void;
    onTaskCreated?: (task: ImageGenerationTaskDto) => Promise<void> | void;
  } = { source: void 0 },
) {
  const { message } = App.useApp();
  const generate_image = useMutation(generateImageMutaionOptions);

  const submit = form.handleSubmit(
    async (values) => {
      lfq('track', 'image.generate_image');

      for (const _ of Array.from({ length: values.quantity ?? 1 })) {
        const task = await generate_image.mutateAsync({
          model: values.model,
          name: values.name ?? void 0,
          images: toFileInputDtoList(values.file_list),
          aspect_ratio: values.aspect_ratio ?? void 0,
          prompt: values.prompt,
          source: options.source,
        });
        await options.onTaskCreated?.(task);
      }

      message.success(options.successMessage ?? '图片生成任务已提交');
      await options.onSuccess?.();
    },
    (error) => {
      console.log(form.getValues());
      console.error(error);
    },
  );

  return { isPending: generate_image.isPending, submit };
}

export function ImageGenerationForm(props: {
  form: ImageGenerationFormInstance;
  isPending: boolean;
  onSubmit: () => void;
  onReset?: () => void;
  showName?: boolean;
  showQuantity?: boolean;
  uploaderId?: string;
  enableAutoPaste?: boolean;
}) {
  const { notification } = App.useApp();
  const sender_ref = useRef<GetRef<typeof Sender>>(null);
  const attachments_ref = useRef<GetRef<typeof Attachments>>(null);
  const [open_overlay] = useOverlay<FileDto>();
  const { control, setValue, getValues } = props.form;
  const accept = useAccept(['.png', '.jpeg', '.jpg', '.bmp']);

  const uploader_id = props.uploaderId ?? 'imageGenerationUploader';
  useAutoPaste(
    props.enableAutoPaste === false
      ? '#disabledImageGenerationUploader'
      : `#${uploader_id}`,
    { filter: accept.filter },
  );

  const sender_header = (
    <Sender.Header
      open
      title="参考图片"
      closable={false}
      classNames={{ content: '!py-0' }}
    >
      <FormControl
        control={control}
        name="file_list"
        render={({ field }) => (
          <Attachments
            id={uploader_id}
            ref={attachments_ref}
            name={field.name}
            classNames={{ list: '-mx-4' }}
            disabled={field.disabled || props.isPending}
            multiple
            items={field.value ?? void 0}
            maxCount={8}
            accept={accept}
            beforeUpload={createBeforeUpload({
              validator: createUploadValidator({ accept: accept.format }),
              onError: ({ title, message }) => {
                notification.error({ title, description: message });
              },
            })}
            onChange={(info) => {
              if (info.file.status === 'error') {
                notification.error({
                  title: '错误',
                  description: `图片 ${info.file.name} 上传失败，请重试`,
                });
              }

              field.onChange(info.fileList);
            }}
            placeholder={(type) =>
              type === 'drop'
                ? { title: '文件放这里' }
                : {
                    icon: <CloudUploadOutlined />,
                    title: '上传文件',
                    description: (
                      <>
                        点击、拖放或<span className="text-blue-500">粘贴</span>
                        文件到此区域进行上传
                      </>
                    ),
                  }
            }
            getDropContainer={() => sender_ref.current?.nativeElement}
          />
        )}
      />
    </Sender.Header>
  );

  return (
    <Form layout="vertical">
      {props.showName !== false && (
        <FormControl
          control={control}
          name="name"
          label="名称"
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              disabled={props.isPending}
            />
          )}
        />
      )}
      <FormControl
        control={control}
        name="prompt"
        render={({ field }) => (
          <Sender
            ref={sender_ref}
            header={sender_header}
            footer={(_, { components }) => {
              const { SendButton, LoadingButton } = components;

              return (
                <div className="flex items-center">
                  <Dropdown
                    trigger={['click']}
                    placement="topLeft"
                    arrow={{ pointAtCenter: true }}
                    menu={{
                      items: [
                        {
                          key: 'image',
                          label: (
                            <div className="flex items-center gap-1">
                              <UploadOutlined />
                              <span>上传</span>
                            </div>
                          ),
                          onClick: () => {
                            attachments_ref.current?.select({ multiple: true });
                          },
                        },
                        {
                          key: 'library',
                          label: (
                            <div className="flex items-center gap-1">
                              <PictureOutlined />
                              <span>我的图片库</span>
                            </div>
                          ),
                          onClick: async () => {
                            const overlay_ref = open_overlay(
                              <UploadedImageListModal />,
                            );
                            const file = await overlay_ref.afterClosed;

                            if (!file) {
                              return;
                            }

                            const url = tryURL(
                              file.oss_object_key,
                              file.oss_host,
                            );

                            if (!url) {
                              return;
                            }

                            setValue('file_list', [
                              ...(getValues('file_list') ?? []),
                              {
                                uid: uuid(),
                                name: file.filename,
                                url: url.toString(),
                                thumbUrl: url.toString(),
                                status: 'done',
                                response: {
                                  id: file.id,
                                  filename: file.filename,
                                  mime: file.mime,
                                  file_url: url.toString(),
                                  created_at: file.created_at,
                                },
                              },
                            ]);
                          },
                        },
                      ],
                    }}
                  >
                    <Button
                      variant="text"
                      color="primary"
                      classNames={{ icon: 'text-base' }}
                      icon={<PaperClipOutlined />}
                      disabled={props.isPending}
                    />
                  </Dropdown>
                  <div className="flex-auto" />
                  <Controller
                    control={control}
                    name="model"
                    render={({ field }) => (
                      <Select
                        {...field}
                        classNames={{ root: '!w-36' }}
                        allowClear={false}
                        disabled={field.disabled || props.isPending}
                        options={Array.from(
                          imageModelSpecs,
                          ([value, spec]) => ({
                            label: spec.name,
                            value,
                          }),
                        )}
                      />
                    )}
                  />
                  <Divider orientation="vertical" />
                  <Controller
                    control={control}
                    name="aspect_ratio"
                    render={({ field: ratio_field }) => (
                      <Segmented<string>
                        ref={ratio_field.ref}
                        name={ratio_field.name}
                        disabled={ratio_field.disabled || props.isPending}
                        value={ratio_field.value ?? void 0}
                        options={['16:9', '4:3', '1:1', '3:4', '9:16']}
                        onBlur={ratio_field.onBlur}
                        onChange={ratio_field.onChange}
                      />
                    )}
                  />
                  <Divider orientation="vertical" />
                  {props.showQuantity !== false && (
                    <>
                      <Controller
                        control={control}
                        name="quantity"
                        render={({ field: quantity_field }) => (
                          <InputNumber
                            classNames={{ root: '!w-20' }}
                            {...quantity_field}
                            min={1}
                            precision={0}
                            max={4}
                            disabled={
                              quantity_field.disabled || props.isPending
                            }
                            suffix="张"
                          />
                        )}
                      />
                      <Divider orientation="vertical" />
                    </>
                  )}
                  <Space>
                    {props.isPending ? (
                      <LoadingButton type="default" />
                    ) : (
                      <SendButton type="primary" />
                    )}
                    <Tooltip title="重置表单">
                      <Button
                        variant="filled"
                        color="primary"
                        shape="circle"
                        classNames={{ icon: 'text-base' }}
                        icon={<ReloadOutlined />}
                        aria-label="重置"
                        disabled={props.isPending}
                        onClick={props.onReset}
                      />
                    </Tooltip>
                  </Space>
                </div>
              );
            }}
            value={field.value}
            placeholder="请输入提示词"
            autoSize={{ minRows: 3 }}
            disabled={field.disabled || props.isPending}
            onBlur={field.onBlur}
            onChange={field.onChange}
            onSubmit={props.onSubmit}
            suffix={false}
          />
        )}
      />
    </Form>
  );
}
